import React, { useState, useContext, useCallback } from 'react'
import { AuthContext } from '@/layout/Auth'
import { TextField, Stack } from '@mui/material'
import { FormCard, ListWrapper, ScrollY, Row } from '../components'
import { RoleCreateInfo, PermissionInfo } from '@/types'
import Form, { FormItem } from '@/components/Form'
import Text from '@/components/Text'
import Button, { ConfirmButton } from '@/components/Button'
import Select from '@/components/Select'
import { Delete } from '@/icon'
import { primary } from '@/constant'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getPermissions, createRoles } from '@/api'
import { Message } from '@cx/ui'

export interface RoleCardProps {
  value: RoleCreateInfo
  onChange: (info: RoleCreateInfo) => void
  permOptions: PermissionInfo[]
  validating?: boolean
  onDelete?: () => void
  onErrorChange?: (hasError: boolean) => void
}

export const RoleCard: React.FC<RoleCardProps> = (props) => {
  const { permOptions, validating, onDelete, value, onChange, onErrorChange } = props

  return (
    <FormCard sx={{ gap: '30px' }}>
      <Form
        sx={{
          width: '100%',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: '16px',
        }}
        initialValue={value}
        // @ts-ignore
        onFormValueChange={onChange}
        // @ts-ignore
        onValidate={onErrorChange}
        validating={validating}
      >
        <FormItem
          label='name'
          text='角色'
          rules={{
            required: true,
            errorMessage: '请输入角色名称',
            validator: (value: string) => value.length > 0,
          }}
        >
          <TextField />
        </FormItem>
        <FormItem
          sx={{ gridColumn: 1 / 2 }}
          label='perms'
          text='权限'
          rules={{
            required: true,
            errorMessage: '请选择角色权限',
            validator: (value: Array<number>) => value.length > 0,
          }}
        >
          <Select
            multiple={true}
            options={
              permOptions?.map((option) => ({
                value: option.id,
                label: option.name,
              })) ?? []
            }
          />
        </FormItem>
      </Form>
      <Delete sx={{ fontSize: '16px', color: primary, cursor: 'pointer' }} onClick={onDelete} />
    </FormCard>
  )
}

export interface RoleCreateItem {
  id: number
  hasError: boolean
  info: RoleCreateInfo
}

export interface RoleCreateProps {
  onCancel: () => void
}

const RoleCreate: React.FC<RoleCreateProps> = (props) => {
  const { onCancel } = props
  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo
  const [creations, setCreations] = useState<RoleCreateItem[]>([
    { id: Date.now(), hasError: true, info: { name: '', perms: [] } },
  ])
  const [validating, setValidation] = useState<boolean>(false)

  const { data: res } = useQuery(
    ['getRoles', orgInfo?.id],
    // @ts-ignore
    () => orgInfo?.id && getPermissions({ id: orgInfo?.id, query: null }),
    {
      enabled: !!orgInfo?.id,
    },
  )

  const createRoleMutation = useMutation(createRoles, {
    onSuccess: () => {
      setValidation(false)
      Message.success('创建成功')
      onCancel()
    },
  })

  const handleCreate = useCallback(() => {
    setValidation(true)
    const hasError = creations.findIndex((item) => item.hasError) > -1
    if (!hasError) {
      const creationsData = creations.map((item) => item.info)
      // @ts-ignore
      createRoleMutation.mutate({ id: orgInfo?.id, roles: creationsData })
    }
  }, [createRoleMutation, creations, orgInfo?.id])

  const handleDelete = useCallback(
    (id: number) => () => {
      const updated = [...creations].filter((item) => item.id !== id)
      setCreations([...updated])
    },
    [creations],
  )

  const handleAdd = useCallback(() => {
    const newItem = {
      id: Date.now(),
      hasError: true,
      info: { name: '', perms: [] as number[] },
    }
    const updated = [...creations]
    updated.push(newItem)
    setValidation(false)
    setCreations([...updated])
  }, [creations])

  const handleUpdate = (id: number, value: RoleCreateInfo) => {
    const updated = [...creations]
    updated.find((item) => item.id === id)!.info = value
    setCreations([...updated])
  }

  const handleErrorStatus = (id: number, hasError: boolean) => {
    const updated = [...creations]
    updated.find((item) => item.id === id)!.hasError = hasError
    setCreations([...updated])
  }

  return (
    <Stack sx={{ height: '100%', gap: '30px' }}>
      <Text weight='bold'>创建角色</Text>
      <ScrollY sx={{ flex: 1 }}>
        <ListWrapper sx={{ gap: '30px' }}>
          {creations?.map((item) => (
            <RoleCard
              key={`invitation-${item.id}`}
              //eslint-disable-next-line react/jsx-no-bind
              onChange={(value) => handleUpdate(item.id, value)}
              value={item.info}
              validating={validating}
              permOptions={res?.data ?? []}
              onDelete={handleDelete(item.id)}
              //eslint-disable-next-line react/jsx-no-bind
              onErrorChange={(hasError: boolean) => handleErrorStatus(item.id, hasError)}
            />
          ))}
          <Button
            type='secondary'
            sx={{
              border: '1px dashed #d9d9d9',
              position: 'sticky',
              bottom: 0,
              background: '#fff',
              width: '100%',
            }}
            onClick={handleAdd}
          >
            添加角色
          </Button>
        </ListWrapper>
      </ScrollY>
      <Row sx={{ justifyContent: 'flex-end', gap: '16px' }}>
        <Button type='secondary' onClick={onCancel}>
          取消
        </Button>
        <ConfirmButton onClick={handleCreate}>确认创建</ConfirmButton>
      </Row>
    </Stack>
  )
}

export default RoleCreate
